<template>
  <v-menu
    class="field-tag-chain-menu"
    :value="value"
    v-bind="menuProps"
    max-width="620"
    offset-y
    :close-on-content-click="false"
    @input="$emit('input', $event)"
  >
    <template v-if="hasActivator" #activator="slotProps">
      <slot name="activator" v-bind="slotProps" />
    </template>
    <v-card :loading="isLoading">
      <v-card-title class="text-h6 pb-2">
        {{ $t("FIELD_TAGS.ANCESTORS_TITLE") }}
      </v-card-title>
      <v-card-text class="pt-2">

        <v-alert v-if="!ancestors.length && !isLoading" dense outlined type="info">
          {{ $t("FIELD_TAGS.NO_ANCESTORS") }}
        </v-alert>

        <div v-else class="tag-chain">
          <div
            v-for="(ancestor, index) in ancestors"
            :key="`ancestor-${index}`"
            class="tag-chain__item"
          >
            <div class="tag-chain__node">
              {{ ancestor.value }}
            </div>
            <v-icon
              v-if="index < ancestors.length - 1|| fieldTag"
              class="tag-chain__arrow"
              color="primary"
            >
              mdi-arrow-down
            </v-icon>
          </div>

          <div v-if="fieldTag" class="tag-chain__node tag-chain__node--current">
            {{ fieldTagValue }}
          </div>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="close">{{ $t("FIELD_TAGS.CANCEL_BUTTON") }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<script>
import { SHOW_SNACK } from "@/store/snack.module";

import { GET_FIELD_TAGS_CHAIN } from "@/store/fieldTags.module";

export default {
  name: "ShowChainDialog",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    fieldTag: {
      type: [Object, Number, String],
      default: null,
    },
    position: {
        type: Object,
        default: () => ({ x: 0, y: 0 }),
    },
  },
  computed: {
    hasActivator() {
      return !!this.$scopedSlots.activator;
    },
    menuProps() {
      if (this.hasActivator) {
        return {};
      }

      return {
        absolute: true,
        positionX: this.position.x,
        positionY: this.position.y,
      };
    },
    fieldTagId() {
        return this.fieldTag && typeof this.fieldTag === "object"
        ? this.fieldTag.id
        : this.fieldTag;
    },
    fieldTagValue() {
        return this.fieldTag && typeof this.fieldTag === "object"
        ? this.fieldTag.value
        : this.fieldTagId;
    },
  },
  data() {
    return {
      ancestors: [],
      isLoading: false,
    };
  },
 watch: {
    value(isOpen) {
      if (isOpen) {
        this.loadData();
        return;
      }
    },
  },
  methods: {
    getErrorMessage(error) {
      if (error && error.errors && typeof error.errors === "object") {
        const firstErrorField = Object.keys(error.errors)[0];
        const firstErrorList = error.errors[firstErrorField];
        if (Array.isArray(firstErrorList) && firstErrorList.length) {
          return firstErrorList[0];
        }
      }
      if (error && error.message) {
        return error.message;
      }
      return this.$t("GENERAL.UNEXPECTED_ERROR");
    },
    close() {
      this.$emit("input", false);
    },
    loadData() {
      if (!this.fieldTag) {
        return;
      }
      this.isLoading = true;

      this.$store
        .dispatch(GET_FIELD_TAGS_CHAIN, { id: this.fieldTagId })
        .then((response) => {
          const chain = Array.isArray(response.chain) ? response.chain : [];
          const lastTag = chain[chain.length - 1];

          // The API includes the requested tag in the chain. The template
          // renders it separately as the current tag, so keep only ancestors
          // in this list to avoid displaying it twice.
          this.ancestors = lastTag && String(lastTag.id) === String(this.fieldTagId)
            ? chain.slice(0, -1)
            : chain;
        })
        .catch((error) => {
          this.$store.dispatch(SHOW_SNACK, {
            type: "error",
            message: this.getErrorMessage(error),
          });
          this.close();
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<style scoped>
.field-tag-chain-menu {
  display: inline-flex;
  flex: 0 0 auto;
  vertical-align: middle;
}

.chain-label {
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.875rem;
  font-weight: 500;
  letter-spacing: 0.01em;
  margin-bottom: 16px;
  text-transform: uppercase;
}

.tag-chain {
  align-items: center;
  display: flex;
  flex-direction: column;
  padding: 4px 12px 8px;
}

.tag-chain__node {
  align-items: center;
  background: #f7f9fc;
  border: 2px solid #c5d0df;
  border-radius: 8px;
  color: rgba(0, 0, 0, 0.82);
  display: flex;
  font-size: 1rem;
  font-weight: 700;
  justify-content: center;
  line-height: 1.35;
  min-height: 56px;
  padding: 12px 20px;
  text-align: center;
  width: 100%;
  word-break: break-word;
}

.tag-chain__node--current {
  background: #e8f1ff;
  border-color: #1976d2;
  color: #0d47a1;
  font-weight: 700;
}

.tag-chain__arrow {
  font-size: 28px;
  height: 36px;
  width: 36px;
}

@media (max-width: 600px) {
  .tag-chain {
    padding-left: 0;
    padding-right: 0;
  }

  .tag-chain__node {
    min-height: 52px;
  }
}
</style>
